<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import GroupingBadge from '@/stay-displayer/grouping/common/GroupingBadge.vue'
import GroupingCard from '@/stay-displayer/grouping/common/GroupingCard.vue'
import GroupingCost from '@/stay-displayer/grouping/common/GroupingCost.vue'
import GroupingErrorBlock from '@/stay-displayer/grouping/common/GroupingErrorBlock.vue'
import GroupingValidationInfo from '@/stay-displayer/grouping/common/GroupingValidationInfo.vue'
import GmeDisplayer from '@/stay-displayer/grouping/smr/GmeDisplayer.vue'
import { hasCriticalError } from '@/stay-displayer/grouping/smr/grouping-validators'
import HospitalizationTypeBadge from '@/stay-displayer/smr/HospitalizationTypeBadge.vue'
import StayComment from '@/stay-displayer/StayComment.vue'
import StayReviewator from '@/stay-displayer/StayReviewator.vue'
import StayValidator from '@/stay-displayer/StayValidator.vue'
import { GroupingCardSmrProps } from '@/store/modules/stay/smr/grouping/types'

const store = useStore()
const { t } = useI18n()

const props = withDefaults(defineProps<GroupingCardSmrProps>(), {
  moneyGap: 0,
  showValidator: true
})

async function comment(comment: string, stayId: number): Promise<void> {
  await store.dispatch(
    'smrStay/commentStay',
    { stayId, comment }
  )
}

const gme = computed(() => props.groupingResult.gme)
const gmt = computed(() => props.groupingResult.gmt)
const cost = computed(() => props.groupingResult.value)
const groupingErrors = computed(() => props.groupingResult.groupingErrors)
const isGroupingValid = computed(() => !hasCriticalError(groupingErrors.value))
const currentStay = computed(() => store.state.smrStay.currentStay)
const isValidated = computed(() => currentStay.value?.status === 'validated')

</script>

<template>
  <div class="card-container">
    <grouping-card class="aside-gme mb-3">
      <template #title>
        {{ t('stayDisplayer.grouping.title') }}
        <hospitalization-type-badge :type="hospitalizationType" />
      </template>
      <template #left>
        <grouping-badge label="GME">
          <gme-displayer
            :gme="gme"
            :has-error="!isGroupingValid"
          />
        </grouping-badge>
        <div
          v-if="isGroupingValid"
          class="cost-row"
        >
          <grouping-cost
            :cost="cost"
            :gmt="gmt"
            :money-gap="moneyGap"
          />
        </div>
      </template>
      <template #right>
        <div class="grouping-card__action-icons">
          <stay-reviewator
            v-if="!isValidated"
            :stay="currentStay"
          />
        </div>
        <grouping-validation-info
          v-if="isValidated"
          :stay="currentStay"
        />
        <stay-validator
          v-if="currentStay && showValidator"
          :stay="currentStay"
          :class="{ 'w-100': !isValidated }"
        />
        <grouping-error-block :errors="groupingErrors" />
      </template>
    </grouping-card>
    <stay-comment
      :stay="currentStay"
      class="mb-3"
      @comment-stay="comment"
    />
  </div>
</template>

<style lang="scss" scoped>
:deep(.grouping-card__title) {
  display: flex;
  align-items: center;
  column-gap: 8px;

  .hosptype-badge {
    font-size: 14px;
    margin-left: auto;
  }
}

.cost-row {
  padding: 5px 0;
}
</style>
